import { FlexBox } from '@airship-platform/jam-web/components';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { DiscountAndFee, useProducts } from '../../data-access/products';
import DiscountSummary from './DiscountSummary';

export const filterDiscounts = (
  discounts: DiscountAndFee[],
  filter: { categoryIds: string[]; names: string[] }
): DiscountAndFee[] =>
  filter.categoryIds.length || filter.names.length
    ? discounts.filter(
        (discount) =>
          (!filter.categoryIds.length ||
            filter.categoryIds.some((id) =>
              discount.categoryIds.includes(id)
            )) &&
          (!filter.names.length ||
            filter.names.some((name) =>
              discount.displayName.toLowerCase().includes(name.toLowerCase())
            ))
      )
    : [];

interface DiscountsListProps {
  children: (discount: DiscountAndFee) => React.ReactNode;
}

const CARD_MIN_WIDTH = 300;
const PREVIEW_LIMIT = 6;

const DiscountsList: React.FC<DiscountsListProps> = ({ children }) => {
  const { productsFilter, availableDiscounts } = useProducts();

  const discounts = useMemo(
    () => filterDiscounts(availableDiscounts, productsFilter),
    [availableDiscounts, productsFilter]
  );

  if (!discounts.length) return null;

  const visibleDiscounts = discounts.slice(0, PREVIEW_LIMIT);
  const displaySeeMore = discounts.length > PREVIEW_LIMIT;

  return (
    <FlexBox flexDirection={'column'} gap={1.5}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_WIDTH}px, 1fr))`,
          gap: 2,
        }}
      >
        {visibleDiscounts.map((discount) => (
          <Card
            key={discount.discountsAndFeesId}
            variant={'bordered'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <DiscountSummary discount={discount} iconSize={'1.5rem'} />
            {children(discount)}
          </Card>
        ))}
      </Box>

      {displaySeeMore && (
        <Button
          component={Link}
          to={'discounts'}
          variant={'contained'}
          color={'softGold'}
          sx={{ alignSelf: 'flex-end' }}
        >
          See all {discounts.length} discounts
        </Button>
      )}
    </FlexBox>
  );
};

export default DiscountsList;
